import React, { useId, useState, useEffect } from "react";

import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { schemeCategory10, text } from "d3";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import Plotly, { LayoutAxis, ModeBarButton, ModeBarDefaultButtons } from "plotly.js";
import Plot from "react-plotly.js";
import { APIResponse } from "services/HttpService";
import { formatTime } from "utils/time-utils";
import { getChartSizeValue } from "utils/utils";
import ChartWrapper from "../chart-wrapper/ChartWrapper";
import copyChartToClipBoard from "../chartUtils";
import { clipboardIcon, csvDownloadIcon, questionMarkIcon } from "../icons";
import {
  AnnotationConfig,
  AxesConfig,
  DatasetScatterProps,
  ImageConfig,
  PlotDataEx,
} from "../interfaces";
import DatasetsTitlesShower from "../DatasetsTitlesShower";
import ExportCsvDialog from "../ExportCsvDialog";

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
  tickformat: "f",
} as Partial<LayoutAxis>;

const layoutConfig = {
  legend: {
    orientation: "h",
    y: -0.3,
  },
  margin: {
    l: 0,
    r: 0,
    b: 0,
    t: 25,
  },
} as Plotly.Layout;

const axisCommonConfig = {
  spikemode: "toaxis",
  showspikes: true,
};

const DatasetScatterChart = (props: DatasetScatterProps) => {
  const uniqueId = useId();

  const [axesConfig, setAxesConfig] = useState<AxesConfig>({});
  const setStatus = useComponentStateCallback(props);

  const [titlesAndOverriddenTitles, setTitlesAndOverriddenTitles] = useState<
    { title: string; overriddenTitle: string }[]
  >([]);
  const [isOverriddenTitleExist, setIsOverriddenTitleExist] = useState<boolean>(false);
  const [titlesDialog, setTitlesDialog] = useState<boolean>(false);
  const [exportCsvDialog, setExportCsvDialog] = useState<boolean>(false);

  const chartConfig: Partial<Plotly.Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: "Copy chart to Clipboard",
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      {
        title: "Download CSV",
        name: "Download CSV",
        icon: csvDownloadIcon,
        click: () => setExportCsvDialog(true),
      },
      isOverriddenTitleExist && {
        title: "Show Titles",
        name: "Show Titles",
        icon: questionMarkIcon,
        click: () => setTitlesDialog(true),
      },
    ].filter(Boolean) as ModeBarDefaultButtons[] | ModeBarButton[] | undefined,
  };

  function flattenArrayOfObjects(): string[] {
    const flattenedArray: string[] = [];

    flattenedArray.push(props.xAxisIdentifier);
    if (typeof props.yAxisIdentifier === "string") {
      flattenedArray.push(props.yAxisIdentifier);
    }
    if (Array.isArray(props.yAxisIdentifier)) {
      props.yAxisIdentifier.forEach(y => {
        if (typeof y === "string") {
          flattenedArray.push(y);
        } else if (typeof y === "object" && y.identifier) {
          flattenedArray.push(y.identifier);
        }
      });
    }
    if (props.colorAxisIdentifier) {
      flattenedArray.push(props.colorAxisIdentifier);
    }
    if (props.sizeAxisIdentifier) {
      flattenedArray.push(props.sizeAxisIdentifier);
    }
    return flattenedArray;
  }

  const linearRegression = (data: [number, number][]) => {
    const n = data.length;
    const sumX = data.reduce((sum, [x]) => sum + x, 0);
    const sumY = data.reduce((sum, [, y]) => sum + y, 0);
    const sumXY = data.reduce((sum, [x, y]) => sum + x * y, 0);
    const sumX2 = data.reduce((sum, [x]) => sum + x * x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return { slope, intercept };
  };

  const linearRegressionLine = ({ slope, intercept }: { slope: number; intercept: number }) => {
    return (x: number) => slope * x + intercept;
  };

  const addTrendline = (xData: number[], yData: number[], color: string) => {
    if (!props.trendLines || xData.length !== yData.length || xData.length === 0) return null;

    const regressionData = xData.map((x, i) => [x, yData[i]]) as [number, number][];
    const regressionLine = linearRegression(regressionData);
    const regression = linearRegressionLine(regressionLine);

    const trendlineX = [Math.min(...xData), Math.max(...xData)];
    const trendlineY = trendlineX.map(regression);

    return {
      x: trendlineX,
      y: trendlineY,
      mode: "lines",
      type: "scatter",
      name: "Trendline",
      line: { color , width: 1, dash: "dash" },
    };
  };

  const promiseHandler = (res: APIResponse<any>) => {
    if (
      Array.isArray(props.yAxisIdentifier) &&
      props.yAxisIdentifier.length > 1 &&
      props.colorAxisIdentifier
    ) {
      console.error("Color and Y array can't co-exist...");
      return []; // ERROR return
    }

    type PlotDataExInformation = { title: string; unit: string };
    type PlotDataExArrays = {
      x: Array<number>;
      y: Array<number>;
      customdata?: Array<string>;
      markerColors?: Array<number>;
      size?: Array<number>;
      symbols?: Array<number | string>;
    };
    function extractArraysFromApiResponse(arrays: {
      xArr: InstantDoubleValue[];
      yArr: InstantDoubleValue[];
      colorArr?: InstantDoubleValue[];
      sizeArr?: InstantDoubleValue[];
    }): PlotDataExArrays {
      const x: Array<number> = [];
      const y: Array<number> = [];
      const markerColors: Array<number> = [];
      const size: Array<number> = [];
      const symbols: Array<number> = [];
      const customdata: Array<string> = [];

      const xMap = new Map<string, number>();
      const yMap = new Map<string, number>();
      const colorMap = new Map<string, number>();
      const sizeMap = new Map<string, number>();

      for (const obj1 of arrays.xArr) {
        xMap.set(obj1.key, obj1.value);
      }

      for (const obj2 of arrays.yArr) {
        yMap.set(obj2.key, obj2.value);
      }

      if (arrays.colorArr) {
        for (const obj3 of arrays.colorArr) {
          colorMap.set(obj3.key, obj3.value);
        }
      }

      if (arrays.sizeArr) {
        for (const obj4 of arrays.sizeArr) {
          sizeMap.set(obj4.key, obj4.value);
        }
      }

      for (const [key, value] of xMap) {
        if (
          yMap.has(key) &&
          (!arrays.colorArr || colorMap.has(key)) &&
          (!arrays.sizeArr || sizeMap.has(key))
        ) {
          x.push(value);
          y.push(yMap.get(key)!);

          if (arrays.colorArr) {
            markerColors.push(colorMap.get(key)!);
          }

          if (arrays.sizeArr) {
            size.push(sizeMap.get(key)!);
          }

          customdata.push(formatTime(key));
        }
      }

      if (props.highlightCount && props.highlightCount > 0) {
        if (x.length > props.highlightCount) {
          for (let i = 0; i < x.length - props.highlightCount; i++) {
            symbols.push(0);
          }
          for (let i = 0; i < props.highlightCount; i++) {
            symbols.push(4);
          }
        } else {
          for (let i = 0; i < x.length; i++) {
            symbols.push(4);
          }
        }
      }

      return { x, y, markerColors, size, customdata, symbols };
    }

    function extractObjectByIdentifier(
      objects: Array<DatasetDataInfoResponse>,
      identifier: string
    ) {
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].identifier === identifier) {
          return objects.splice(i, 1)[0];
        }
      }

      return undefined;
    }
    function truncateMilliseconds(arr: InstantDoubleValue[]): InstantDoubleValue[] {
      return arr.map(obj => {
        const newObj: InstantDoubleValue = { key: "", value: obj.value };
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            const date = new Date(obj.key);
            date.setMilliseconds(0);
            newObj.key = date.toISOString();
          }
        }
        return newObj;
      });
    }

    const symbolWidthLogic = (symbols?: Array<number | string>) => {
      if (symbols) {
        if (props.sizeAxisIdentifier) {
          return symbols.map(() => 1);
        }
        return symbols.map(e => (e === 0 ? 0 : 2));
      }

      return undefined;
    };

    const createPlotDataExObject = (
      info: PlotDataExInformation,
      arrs: PlotDataExArrays,
      index: number
    ): PlotDataEx => {
      const color = schemeCategory10[index % 10];
      const markerColors = new Array(arrs.x.length).fill(color);
      if (props.highlightCount && props.highlightCount <= markerColors.length) {
        for (
          let i = markerColors.length - 1;
          i >= markerColors.length - props.highlightCount;
          i--
        ) {
          markerColors[i] = "red";
        }
      }

      return {
        name: info.title,
        type: "scatter",
        mode: "markers",
        hovertemplate: `<b>X:</b> %{x:.3f} <b>Y:</b> %{y:.3f} <br> <b>Date:</b> %{customdata}<extra></extra>`,
        unit: info.unit,
        marker: {
          color:
            arrs.markerColors && arrs.markerColors.length > 0 ? arrs.markerColors : markerColors,
          showscale: !!(arrs.markerColors && arrs.markerColors.length > 0),
          size: arrs.size && arrs.size.length > 0 ? arrs.size : undefined,
          symbol: arrs.symbols && arrs.symbols.length > 0 ? arrs.symbols : undefined,
          line: {
            color:
              arrs.symbols && arrs.symbols.length > 0
                ? arrs.symbols.map(e => (e === 0 ? "white" : "red"))
                : undefined,
            width: symbolWidthLogic(arrs.symbols),
          },
        },
        showlegend: true,
        x: arrs.x,
        y: arrs.y,
        customdata: arrs.customdata,
      } as PlotDataEx;
    };

    // removes milliseconds for all returned data
    res.data = res.data.map((ds: DatasetDataInfoResponse) => {
      ds.data = truncateMilliseconds(ds.data);
      return ds;
    });

    const xObject = extractObjectByIdentifier(res.data, props.xAxisIdentifier);

    if (!xObject) {
      console.error("X dataset is not defined...");
      return [];
    }

    let colorObject: DatasetDataInfoResponse | undefined;
    if (props.colorAxisIdentifier) {
      colorObject = extractObjectByIdentifier(res.data, props.colorAxisIdentifier);
    }

    let sizeObject: DatasetDataInfoResponse | undefined;
    if (props.sizeAxisIdentifier) {
      sizeObject = extractObjectByIdentifier(res.data, props.sizeAxisIdentifier);
    }
    if (res.data.length === 0) {
      console.error("Y dataset(s) is not defined");
      return [];
    }

    const checkUnits = () => {
      const { unit } = res.data[0];

      res.data.forEach((ob: DatasetDataInfoResponse) => {
        if (ob.unit !== unit) {
          return "ERROR";
        }
      });

      return unit;
    };
    createAxesConfig(xObject.unit as string, checkUnits());
    const datasets = res.data.map((ds: DatasetDataInfoResponse, index: number) => {
      const overriddenTitle = Array.isArray(props.yAxisIdentifier)
        ? (
            props.yAxisIdentifier.find(
              item => typeof item !== "string" && item.identifier === ds.identifier
            ) as { overriddenTitle?: string }
          )?.overriddenTitle || ""
        : "";

      const plotData = createPlotDataExObject(
        {
          title: overriddenTitle || ds.title,
          unit: ds.unit as string,
        },
        extractArraysFromApiResponse({
          xArr: xObject.data,
          yArr: ds.data,
          colorArr: colorObject?.data,
          sizeArr: sizeObject?.data,
        }),
        index
      );
      
      const color = schemeCategory10[index % 10];

      const xValues = plotData.x as number[];
      const yValues = plotData.y as number[];
      const trendlineData = addTrendline(xValues, yValues, color);

      return [plotData, trendlineData].filter(Boolean);
    });

    setTitlesAndOverriddenTitles(
      datasets.map(d => ({ title: d[0].name, overriddenTitle: d[0].name }))
    );
    return datasets.flat();
  };

  useEffect(() => {
    const hasOverriddenTitle = titlesAndOverriddenTitles.some(
      dataset => dataset.overriddenTitle !== ""
    );
    setIsOverriddenTitleExist(hasOverriddenTitle);
  }, [titlesAndOverriddenTitles]);

  const createPhotoConfig = (photo?: ImageConfig) => {
    if (!photo || !photo.source) return {};

    const { source, x = 0, y = 0, sizex = 1, sizey = 1, opacity = 1 } = photo;

    return { source, x, y, sizex, sizey, opacity };
  };

  const createAnnotationsConfig = (annotations?: Array<AnnotationConfig>) => {
    if (!annotations) return [];

    return annotations.map((annotation: AnnotationConfig) => {
      return { showarrow: false, ...annotation } as Partial<Plotly.Annotations>;
    });
  };

  const createAxesConfig = (xUnit: string, yUnit: string) => {
    const axes = axesConfig;

    axes.xaxis = {
      title: {
        text: xUnit,
        standoff: 10,
      },
      range: props.axisConfig?.xRange,
      ...axisCommonConfig,
    } as LayoutAxis;

    axes.yaxis = {
      title: {
        text: yUnit,
        standoff: 10,
      },
      range: props.axisConfig?.yRange,
      ...commonAxisConfig,
    } as LayoutAxis;
    setAxesConfig({ ...axes });
  };

  if (
    Array.isArray(props.yAxisIdentifier) &&
    props.yAxisIdentifier.length > 1 &&
    props.colorAxisIdentifier
  ) {
    return <div className="error-div">Config error</div>;
  }

  return (
    <ChartWrapper
      uniqueKey={uniqueId}
      identifiers={flattenArrayOfObjects()}
      showDateSelection
      promiseHandler={promiseHandler}
      updateFrequency={props.updateFrequency}
      height={props.height}
      chartConfig={chartConfig}
      loadConf={props.loadConf}
      hideRefresh={props.hideRefresh}
      referenceTime={props.referenceTime}
      dynamicHeight={props.dynamicHeight}
    >
      {(wrapperProps: any) => (
        <>
          {titlesDialog && (
            <DatasetsTitlesShower
              dialog={titlesDialog}
              setDialog={setTitlesDialog}
              titlesAndOverriddenTitles={titlesAndOverriddenTitles}
            />
          )}
          {exportCsvDialog && (
            <ExportCsvDialog
              dialog={exportCsvDialog}
              setDialog={setExportCsvDialog}
              data={wrapperProps.data}
              chartType="SCATTER"
            />
          )}
          <Plot
            data={[...wrapperProps.data]}
            className="ea-plotly-scatter-chart"
            config={{ ...wrapperProps.chartConfig }}
            layout={{
              ...axesConfig,
              ...layoutConfig,
              width: getChartSizeValue(props.width || wrapperProps.width),
              height: wrapperProps.height,
              images: [
                {
                  layer: "below",
                  yanchor: "bottom", // Positioning anchor (left, center, right)
                  sizing: "stretch",
                  xref: "x",
                  yref: "y",
                  ...createPhotoConfig(props.imageConfig),
                },
              ],
              annotations: [...createAnnotationsConfig(props.annotationsConfig)],
            }}
            onInitialized={() => setStatus({ state: "finished" })}
          />
        </>
      )}
    </ChartWrapper>
  );
};

export default DatasetScatterChart;
